import React, { useCallback, useEffect, useState } from "react";
import { X, Loader2, Archive, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Card, Btn } from "../UI";
import { usd, errMsg, valueLabel, dateRange } from "./format";
import { FunnelBars } from "./FunnelBars";
import { StaffPanel } from "./StaffPanel";
import { AllocatePanel, PrintPanel } from "./PrintPanel";

const Chip = ({ label, value, accent, testid }) => (
  <div className="rounded-lg border border-white/10 px-3 py-2" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`font-display text-lg font-bold ${accent ? "text-amber-300" : ""}`}>{value}</div>
  </div>
);

export const EventDrawer = ({ eventId, onClose, onChanged }) => {
  const [ev, setEv] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/promotions/events/${eventId}`); setEv(data); }
    catch (e) { toast.error(errMsg(e, "Could not load the trade show")); onClose(); }
  }, [eventId, onClose]);
  useEffect(() => { load(); }, [load]);
  const reload = () => { load(); onChanged(); };

  const toggleActive = async () => {
    try { await api.put(`/promotions/events/${eventId}`, { active: !ev.active }); toast.success(ev.active ? "Trade show archived" : "Trade show restored"); reload(); }
    catch (e) { toast.error(errMsg(e, "Update failed")); }
  };

  const s = ev?.stats || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="event-drawer">
      <Card className="w-full max-w-4xl !p-6 max-h-[92vh] overflow-y-auto">
        {!ev ? (
          <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-display text-lg font-bold" data-testid="event-drawer-title">{ev.name}</div>
                <div className="text-xs text-slate-400 mt-1">
                  {dateRange(ev.starts_at, ev.ends_at)}{ev.venue ? ` · ${ev.venue}` : ""}{ev.booth ? ` · Booth ${ev.booth}` : ""}
                  {ev.promotion && <> · <span className="text-amber-300">{ev.promotion.name} · {valueLabel(ev.promotion)}</span>{!ev.promotion.active && <span className="text-rose-300"> (promotion inactive)</span>}</>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Btn variant="secondary" onClick={toggleActive} data-testid="event-toggle-active-btn">
                  {ev.active ? <><Archive className="w-4 h-4" /> Archive</> : <><RotateCcw className="w-4 h-4" /> Restore</>}
                </Btn>
                <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="event-drawer-close"><X className="w-5 h-5" /></button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Chip label="Codes" value={s.live || 0} testid="event-chip-codes" />
              <Chip label="Scanned" value={s.scanned || 0} testid="event-chip-scanned" />
              <Chip label="Claimed" value={s.claimed || 0} testid="event-chip-claimed" />
              <Chip label="Redeemed" value={s.redeemed || 0} testid="event-chip-redeemed" />
              <Chip label="Value redeemed" value={usd(s.redeemed_value)} accent testid="event-chip-value" />
            </div>
            <FunnelBars stats={s} testid="event-funnel" />

            <StaffPanel ev={ev} reload={reload} />
            <AllocatePanel ev={ev} reload={reload} />
            <PrintPanel ev={ev} reload={reload} />
          </div>
        )}
      </Card>
    </div>
  );
};
